import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

import determineHeaderImage from '../Layout/Header/determineHeaderImage';
import determineCartCount from '../../helpers/cart/determineCartCount';
import siteInfoById from '../../constants/siteInfoById';
import deepCopy from '../../utilities/deepCopy';
import SubtotalPanel from './SubtotalPanel';
import CountdownTimer from '../CountdownTimer';
import CheckoutForm from './CheckoutForm';
import getSubmitErrors from './validators/getSubmitErrors';
import checkoutConfiguration from './constants/checkoutConfiguration';
import Loader from '../../helpers/Loader';
import isDataEmpty from '../../helpers/isDataEmpty';
import { encryptCheckout } from '../../utilities/encryptionUtility';

import { submitCheckout } from '../../containers/Checkout/checkoutSlice';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

const CheckoutLanding = (props) => {
	const {
		cartData,
		checkoutResponse,
		checkoutError,
		checkoutIsLoading,
		customerId,
		customerIp,
		ipLocation,
		sessionId,
		siteId,
		submitCheckout,
		subtotal
	} = props;

	const navigate = useNavigate();

	React.useEffect(() => {
		if (!isDataEmpty(checkoutResponse)) {
			navigate('/checkout/complete');
		} else if (checkoutError) {
			navigate('/checkout/error');
		}

		if (isDataEmpty(cartData)) {
			navigate('/');
		}
	}, [checkoutResponse, checkoutError]);

	const [checkoutInformation, setCheckoutInformation] = React.useState(
		deepCopy(checkoutConfiguration)
	);
	const [submitErrors, setSubmitErrors] = React.useState();

	const handleUpdateForm = (property, value) => {
		const updatedForm = deepCopy(checkoutInformation);

		updatedForm[property] = value;

		if (submitErrors) {
			const updatedSubmitErrors = submitErrors.filter((error) => {
				return error.type !== property;
			});

			setSubmitErrors(updatedSubmitErrors);
		}

		setCheckoutInformation((previousConfiguration) => ({
			...previousConfiguration,
			[property]: value
		}));
	};

	const handleFilterOfCartInventory = (cartData) => {
		return cartData.map((item) => {
			return {
				id: item.id,
				sku: item.sku,
				price: item.price,
				quantity: item.quantity,
				product: `$${item.amount} ${item.company} Gift Card`
			};
		});
	};

	const handleSubmit = async () => {
		const errors = getSubmitErrors(checkoutInformation);

		if (errors.length) {
			setSubmitErrors(errors);
		} else {
			setSubmitErrors();
			await submitCheckout({
				transactionRequest: {
					transactionType: 'authOnlyTransaction',
					siteId,
					customerId,
					sessionId,
					customerIp,
					ipLocation,
					totalAmount: subtotal,
					orderLineItems: handleFilterOfCartInventory(cartData),
					payment: {
						creditCard: encryptCheckout({
							cardNumber: checkoutInformation.cardNumber,
							expirationDate: checkoutInformation.expirationDate,
							cardCode: checkoutInformation.cardCode
						})
					}
				},
				billTo: {
					firstName: checkoutInformation.firstName,
					lastName: checkoutInformation.lastName,
					company: checkoutInformation.company,
					address: checkoutInformation.address,
					city: checkoutInformation.city,
					state: checkoutInformation.state,
					zip: checkoutInformation.zip,
					country: checkoutInformation.country,
					email: checkoutInformation.email,
					phoneNumber: checkoutInformation.phoneNumber
				},
				supplementalInformation: {
					electronicFulfillment: checkoutInformation.electronicFulfillment,
					additionalNotes: checkoutInformation.additionalNotes
				}
			});
		}
	};

	if (checkoutIsLoading) {
		return <Loader />;
	}

	return (
		<Stack
			alignItems='center'
			spacing={2}
			mb={9}
			className='filterbg'
			py={1}
			sx={{
				px: { xs: 0, lg: 1 },
				mt: { xs: 7, sm: 10 },
				background: (theme) => theme.palette.common.white
			}}
		>
			<Stack
				direction='row'
				component='header'
				alignItems='center'
				justifyContent='center'
				className='filterbg'
				py={2}
				sx={{
					width: '100%',
					background: (theme) => theme.palette.common.white,
					position: 'fixed',
					top: 0,
					zIndex: 99,
					px: { xs: 1, sm: 0 }
				}}
			>
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='center'
					sx={{
						width: { xs: '98%', sm: '96%', lg: '85%', xl: '65%' },
						'&:hover': {
							opacity: 0.8
						}
					}}
				>
					<Stack>
						<Link to='/' onClick={() => window.scrollTo({ top: 0 })}>
							<Box
								component='img'
								src={determineHeaderImage(siteId)}
								alt={`${siteInfoById[siteId].name} Logo`}
								className='filter'
								sx={{
									height: { xs: '39px', md: '60px' }
								}}
							/>
						</Link>
					</Stack>

					<IconButton
						className='filter'
						sx={{ color: 'common.black' }}
						onClick={() => {
							window.scrollTo({ top: 0 });
							navigate('/cart');
						}}
					>
						<Badge badgeContent={determineCartCount(cartData)} color='primary'>
							<ShoppingCartOutlinedIcon sx={{ fontSize: { xs: '28px', sm: '31px', md: '40px' } }} />
							<Typography sx={{ display: 'none' }}>Cart</Typography>
						</Badge>
					</IconButton>
					<Stack
						sx={{
							position: 'absolute',
							top: {
								xs: '25px',
								md: '30px'
							},
							right: {
								xs: '75px',
								sm: '95px',
								md: '109px',
								lg: '200px',
								xl: '40px'
							}
						}}
					>
						<CountdownTimer />
					</Stack>
				</Stack>
			</Stack>

			<Stack
				justifyContent='center'
				spacing={12}
				sx={{
					flexDirection: { xs: 'column', md: 'row' },
					pb: '50px',
					width: { xs: '95%', lg: '96%', xl: '65%' }
				}}
			>
				<CheckoutForm
					checkoutInformation={checkoutInformation}
					submitErrors={submitErrors}
					setSubmitErrors={setSubmitErrors}
					handleUpdateForm={handleUpdateForm}
				/>
				<SubtotalPanel
					cartData={cartData}
					subtotal={subtotal}
					checkoutInformation={checkoutInformation}
					handleUpdateForm={handleUpdateForm}
					handleSubmit={handleSubmit}
					submitErrors={submitErrors}
				/>
			</Stack>
		</Stack>
	);
};

CheckoutLanding.propTypes = {
	cartData: PropTypes.array,
	checkoutResponse: PropTypes.object,
	checkoutError: PropTypes.string,
	checkoutIsLoading: PropTypes.bool,
	customerId: PropTypes.number,
	customerIp: PropTypes.string,
	ipLocation: PropTypes.string,
	sessionId: PropTypes.string,
	siteId: PropTypes.number,
	submitCheckout: PropTypes.func,
	subtotal: PropTypes.number
};

const mapStateToProps = (state) => {
	return {
		cartData: state.cartSlice.cartData,
		checkoutResponse: state.checkoutSlice.data,
		checkoutError: state.checkoutSlice.error,
		checkoutIsLoading: state.checkoutSlice.isSubmitting,
		customerId: state.authSlice.customerId,
		customerIp: state.authSlice.customerIp,
		ipLocation: state.authSlice.ipLocation,
		sessionId: state.authSlice.sessionId,
		siteId: state.authSlice.siteId,
		subtotal: state.cartSlice.subtotal
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		submitCheckout: (payload) => {
			dispatch(submitCheckout(payload));
		}
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutLanding);
